import { SurveyEngine } from './survey-engine.js';
import { requestSurveyDisplay } from './survey-session.js';
import { generateUUID } from '../utils/uuid.js';

function isPreviewType(surveyType) {
    return typeof surveyType === 'string' && surveyType.startsWith('preview');
}

/**
 * Creates the SurveyEngine and hands it the view request (surveys/view) as a
 * display gate. The engine asks the server only at the moment the survey is
 * actually about to show: a popup right away, a popup that starts minimized
 * when the visitor opens the badge, an embed once its container is in the
 * viewport. Previews skip the server round trip (they never track anything).
 *
 * Resolves to the engine once it is shown, rendered as a badge or waiting
 * for its container, and to null when the server denied the display or the
 * survey is already active. A badge or embed engine may still abort later.
 */
export function launchSurvey(survey, surveyType = 'onsite', storage = null, can = {}, parentNode = null) {
    if (isPreviewType(surveyType)) {
        return Promise.resolve(new SurveyEngine(survey, surveyType, 0, parentNode, storage, can));
    }

    const surveyId = survey.survey_id;
    if (SurveyEngine.activeSurveys.has(surveyId)) {
        return Promise.resolve(null);
    }

    const userId = (storage && storage.get('ph_user_id')) || generateUUID();

    return new Promise((resolve) => {
        let engine = null;
        // The decision may arrive synchronously (badge) before the constructor
        // returned, so the engine reference is resolved a tick later
        const onDisplayDecision = (shown) => Promise.resolve().then(() => resolve(shown ? engine : null));
        try {
            engine = new SurveyEngine(survey, surveyType, 0, parentNode, storage, can, {
                displayGate: () => requestSurveyDisplay(survey, storage, userId),
                onDisplayDecision,
            });
        } catch (error) {
            console.warn('[Survey] Failed to launch survey:', surveyId, error);
            SurveyEngine.activeSurveys.delete(surveyId);
            resolve(null);
        }
    });
}
